import React, { useState } from 'react';
import {
  Building, User, Award, Target, CheckCircle, ArrowRight, ArrowLeft,
  Upload, Camera, MapPin, Globe, Phone, Mail, Users, Calendar,
  Heart, Leaf, Shield, Star, Sparkles, Zap, TrendingUp, X,
  Plus, Trash2, FileText, ExternalLink, Play, Pause, Volume2
} from 'lucide-react';

const Onboarding = ({ company, setShowOnboarding }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isCompleted, setIsCompleted] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    companyName: company?.name || '',
    industry: company?.industry || '',
    size: company?.size || '',
    founded: '',
    website: '',
    description: '',
    
    // Step 2: Contact & Location
    contactPerson: {
      name: company?.contactPerson?.name || '',
      role: company?.contactPerson?.role || '',
      email: company?.contactPerson?.email || ''
    },
    location: {
      address: '',
      city: '',
      country: 'France',
      postalCode: ''
    },
    phone: '',
    
    // Step 3: Values & Certifications
    mission: '',
    values: [],
    certifications: [],
    sustainabilityGoals: [],
    
    // Step 4: Project Preferences
    projectTypes: [],
    budgetRange: '',
    collaborationStyle: '',
    communicationPrefs: [],
    
    // Step 5: Verification
    businessDocument: null,
    certificationDocuments: [],
    terms: false,
    newsletter: true
  });

  const totalSteps = 5;

  const steps = [
    {
      id: 1,
      title: "Informations entreprise",
      subtitle: "Présentez votre entreprise",
      icon: Building,
      color: "from-blue-500 to-cyan-500"
    },
    {
      id: 2,
      title: "Contact & localisation",
      subtitle: "Vos coordonnées",
      icon: User,
      color: "from-green-500 to-emerald-500"
    },
    {
      id: 3,
      title: "Valeurs & engagements",
      subtitle: "Vos valeurs éthiques",
      icon: Heart,
      color: "from-purple-500 to-pink-500"
    },
    {
      id: 4,
      title: "Préférences de collaboration",
      subtitle: "Votre façon de travailler",
      icon: Target,
      color: "from-orange-500 to-yellow-500"
    },
    {
      id: 5,
      title: "Vérification & finalisation",
      subtitle: "Dernière étape",
      icon: Award,
      color: "from-emerald-500 to-teal-500"
    }
  ];

  const industryOptions = [
    "GreenTech", "Énergie Renouvelable", "Économie Circulaire", "AgriTech",
    "MedTech", "EdTech", "FinTech Responsable", "Mobilité Durable",
    "Construction Durable", "Mode Éthique", "Alimentation Durable", "Autre"
  ];

  const companySizeOptions = [
    "1-10 employés", "11-50 employés", "51-200 employés", 
    "201-1000 employés", "1000+ employés"
  ];

  const certificationOptions = [
    "B-Corp", "ISO 26000", "ISO 14001", "Label NR", "Carbon Trust",
    "Fair Trade", "BCORP Impact", "GRI Standards", "UN Global Compact",
    "Cradle to Cradle", "LEED", "Autre"
  ];

  const projectTypeOptions = [
    "Design UX/UI", "Développement Web", "Marketing Digital", "Data Science",
    "Consulting RSE", "Stratégie", "Communication", "Formation",
    "Rédaction", "Traduction", "Photographie", "Vidéo"
  ];

  const handleInputChange = (field, value, subField = null) => {
    if (subField) {
      setFormData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [subField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleArrayAdd = (field, value) => {
    if (value.trim() && !formData[field].includes(value.trim())) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeOnboarding = () => {
    setIsCompleted(true);
    setTimeout(() => {
      setShowOnboarding(false);
    }, 3000);
  };

  const ProgressBar = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-gray-500">
          Étape {currentStep} sur {totalSteps}
        </h2>
        <span className="text-sm text-gray-500">
          {Math.round((currentStep / totalSteps) * 100)}% complété
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        ></div>
      </div>
    </div>
  );

  const StepHeader = ({ step }) => (
    <div className="text-center mb-8">
      <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-r ${step.color} rounded-2xl flex items-center justify-center shadow-lg`}>
        <step.icon className="w-8 h-8 text-white" />
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h1>
      <p className="text-gray-600">{step.subtitle}</p>
    </div>
  );

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full text-center">
          <div className="bg-white rounded-3xl p-12 shadow-2xl border border-green-100">
            <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Bienvenue sur LinkUp ! 🎉
            </h1>
            
            <p className="text-gray-600 text-lg mb-8">
              Votre profil entreprise a été créé avec succès. Vous pouvez maintenant commencer à collaborer avec des freelances engagés qui partagent vos valeurs.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <Sparkles className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900 mb-1">Matching IA</h3>
                <p className="text-sm text-gray-600">Trouvez les freelances parfaits grâce à notre IA</p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                <Heart className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900 mb-1">Valeurs alignées</h3>
                <p className="text-sm text-gray-600">Collaborez avec des talents qui partagent vos valeurs</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                <Shield className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900 mb-1">Certifié responsable</h3>
                <p className="text-sm text-gray-600">Accédez à un réseau de professionnels certifiés</p>
              </div>
            </div>
            
            <div className="text-sm text-gray-500">
              Redirection vers le dashboard dans quelques secondes...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-400 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">L</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">LinkUp</h1>
              <p className="text-sm text-gray-500">Configuration de votre profil entreprise</p>
            </div>
          </div>
          
          <button
            onClick={() => setShowOnboarding(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="p-8">
            <ProgressBar />
            <StepHeader step={steps[currentStep - 1]} />

            {/* Step 1: Company Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom de l'entreprise *
                    </label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Ex: GreenTech Solutions"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secteur d'activité *
                    </label>
                    <select
                      value={formData.industry}
                      onChange={(e) => handleInputChange('industry', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez un secteur</option>
                      {industryOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Taille de l'entreprise *
                    </label>
                    <select
                      value={formData.size}
                      onChange={(e) => handleInputChange('size', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez la taille</option>
                      {companySizeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Année de création
                    </label>
                    <input
                      type="number"
                      value={formData.founded}
                      onChange={(e) => handleInputChange('founded', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="2020"
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site web
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="https://votre-entreprise.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description de l'entreprise *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Décrivez brièvement votre entreprise, ses activités et ses valeurs..."
                  />
                </div>
              </div>
            )}

            {/* Step 2: Contact & Location */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Personne de contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet *</label>
                      <input
                        type="text"
                        value={formData.contactPerson.name}
                        onChange={(e) => handleInputChange('contactPerson', e.target.value, 'name')}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Jean Dupont"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Poste *</label>
                      <input
                        type="text"
                        value={formData.contactPerson.role}
                        onChange={(e) => handleInputChange('contactPerson', e.target.value, 'role')}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="DRH, CEO, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        value={formData.contactPerson.email}
                        onChange={(e) => handleInputChange('contactPerson', e.target.value, 'email')}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="jean@entreprise.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Localisation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                      <input
                        type="text"
                        value={formData.location.address}
                        onChange={(e) => handleInputChange('location', e.target.value, 'address')}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="123 Rue de la Paix"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ville *</label>
                      <input
                        type="text"
                        value={formData.location.city}
                        onChange={(e) => handleInputChange('location', e.target.value, 'city')}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Paris"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Code postal</label>
                      <input
                        type="text"
                        value={formData.location.postalCode}
                        onChange={(e) => handleInputChange('location', e.target.value, 'postalCode')}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="75001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pays *</label>
                      <select
                        value={formData.location.country}
                        onChange={(e) => handleInputChange('location', e.target.value, 'country')}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="France">France</option>
                        <option value="Belgique">Belgique</option>
                        <option value="Suisse">Suisse</option>
                        <option value="Canada">Canada</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="+33 1 23 45 67 89"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Values & Certifications */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mission de l'entreprise *
                  </label>
                  <textarea
                    value={formData.mission}
                    onChange={(e) => handleInputChange('mission', e.target.value)}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Quelle est la mission principale de votre entreprise ?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Certifications & Labels responsables
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {certificationOptions.map(cert => (
                      <button
                        key={cert}
                        onClick={() => handleArrayToggle('certifications', cert)}
                        className={`p-3 text-sm font-medium rounded-xl border-2 transition-all duration-200 ${
                          formData.certifications.includes(cert)
                            ? 'bg-green-100 border-green-500 text-green-700'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-green-300'
                        }`}
                      >
                        {cert}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Heart className="w-5 h-5 mr-2 text-purple-500" />
                    Vos valeurs entreprise
                  </h3>
                  <div className="space-y-3">
                    {formData.values.map((value, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                        <span className="text-gray-700">{value}</span>
                        <button
                          onClick={() => handleArrayRemove('values', index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        placeholder="Ajouter une valeur..."
                        className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleArrayAdd('values', e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <button
                        onClick={(e) => {
                          const input = e.target.previousElementSibling;
                          handleArrayAdd('values', input.value);
                          input.value = '';
                        }}
                        className="px-4 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Project Preferences */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Types de projets recherchés
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {projectTypeOptions.map(type => (
                      <button
                        key={type}
                        onClick={() => handleArrayToggle('projectTypes', type)}
                        className={`p-3 text-sm font-medium rounded-xl border-2 transition-all duration-200 ${
                          formData.projectTypes.includes(type)
                            ? 'bg-blue-100 border-blue-500 text-blue-700'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget moyen par projet
                    </label>
                    <select
                      value={formData.budgetRange}
                      onChange={(e) => handleInputChange('budgetRange', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez une fourchette</option>
                      <option value="500-2000">500€ - 2 000€</option>
                      <option value="2000-5000">2 000€ - 5 000€</option>
                      <option value="5000-10000">5 000€ - 10 000€</option>
                      <option value="10000+">10 000€+</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Style de collaboration préféré
                    </label>
                    <select
                      value={formData.collaborationStyle}
                      onChange={(e) => handleInputChange('collaborationStyle', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez un style</option>
                      <option value="autonome">Autonome - Briefing puis livraison</option>
                      <option value="collaboratif">Collaboratif - Échanges réguliers</option>
                      <option value="agile">Agile - Sprints et itérations</option>
                      <option value="sur-mesure">Sur-mesure selon le projet</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Verification */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-yellow-500" />
                    Vérification d'entreprise
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Pour garantir la qualité de notre réseau, nous vérifions l'authenticité de chaque entreprise.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Document officiel (KBIS, statuts, etc.)
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-green-400 transition-colors duration-200">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Cliquez pour télécharger ou glissez-déposez</p>
                        <p className="text-xs text-gray-500 mt-1">PDF, JPG ou PNG - Max 5MB</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={formData.terms}
                      onChange={(e) => handleInputChange('terms', e.target.checked)}
                      className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="terms" className="text-sm text-gray-700">
                      J'accepte les{' '}
                      <a href="#" className="text-green-600 hover:text-green-700 underline">
                        conditions d'utilisation
                      </a>{' '}
                      et la{' '}
                      <a href="#" className="text-green-600 hover:text-green-700 underline">
                        politique de confidentialité
                      </a>
                      *
                    </label>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="newsletter"
                      checked={formData.newsletter}
                      onChange={(e) => handleInputChange('newsletter', e.target.checked)}
                      className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="newsletter" className="text-sm text-gray-700">
                      Je souhaite recevoir des conseils et actualités sur le freelancing responsable
                    </label>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                  <h3 className="text-lg font-semibold text-green-700 mb-2">Presque terminé ! 🎉</h3>
                  <p className="text-green-600 text-sm">
                    Votre profil sera vérifié sous 24h. Vous recevrez un email de confirmation une fois validé.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-8">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                  currentStep === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Précédent</span>
              </button>

              {currentStep < totalSteps ? (
                <button
                  onClick={nextStep}
                  className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span>Suivant</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={completeOnboarding}
                  disabled={!formData.terms}
                  className={`flex items-center space-x-2 px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl ${
                    formData.terms
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Finaliser l'inscription</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;